import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  ArrowUpCircle, 
  ArrowDownCircle, 
  Clock, 
  DollarSign,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { formatDateRu } from '../../utils/formatUtils';

const TradeHistory = ({ trades = [] }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const { tt } = useLanguage();
  const tradesPerPage = 10;

  // DEBUG: Логируем что получили
  useEffect(() => {
    console.log('📊 TradeHistory received trades:', {
      total: trades.length,
      active: trades.filter(t => t.status === 'active').length,
      firstThree: trades.slice(0, 3).map(t => ({ id: t.id, status: t.status, asset: t.asset }))
    });
  }, [trades]);

  // Обновляем текущее время каждую секунду для корректного отображения оставшегося времени
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Функция для форматирования времени в локальной временной зоне пользователя
  const formatLocalTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Используем переданные trades напрямую без дополнительной фильтрации
  // Фильтрация уже выполнена в родительском компоненте (TradingApp)
  const filteredTrades = trades;

  // Пагинация
  const totalPages = Math.ceil(filteredTrades.length / tradesPerPage);
  const startIndex = (currentPage - 1) * tradesPerPage;
  const endIndex = startIndex + tradesPerPage;
  const currentTrades = filteredTrades.slice(startIndex, endIndex);

  // Сброс на первую страницу при изменении trades
  useEffect(() => {
    setCurrentPage(1);
  }, [trades]);

  const getStatusBadge = (trade) => {
    switch (trade.status) {
      case 'active':
        return <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded">{tt('Активна')}</span>;
      case 'won':
        return <span className="bg-green-600 text-white text-xs px-2 py-1 rounded">{tt('Прибыль')}</span>;
      case 'lost':
        return <span className="bg-red-600 text-white text-xs px-2 py-1 rounded">{tt('Убыток')}</span>;
      case 'expired':
        return <span className="bg-yellow-600 text-white text-xs px-2 py-1 rounded">{tt('Истекла')}</span>;
      default:
        return <span className="bg-gray-600 text-white text-xs rounded">{tt('Неизвестно')}</span>;
    }
  };

  const formatProfit = (trade) => {
    // ИСПРАВЛЕНО: используем profit_loss вместо profit
    const profitValue = trade.profit_loss || 0;
    
    if (trade.status === 'active') {
      return <span className="text-gray-400">{tt('Активна')}</span>;
    }
    
    if (trade.status === 'won') {
      // Прибыль = profit_loss (85% от ставки)
      return (
        <span className="text-green-400">
          +${profitValue.toFixed(2)}
        </span>
      );
    }
    
    if (trade.status === 'lost') {
      // Убыток = вся ставка
      return (
        <span className="text-red-400">
          -${trade.amount.toFixed(2)}
        </span>
      );
    }
    
    return '---';
  };

  const formatTime = (timestamp) => {
    return formatDateRu(timestamp);
  };

  const getTimeRemaining = (trade) => {
    if (!trade || trade.status !== 'active') return null;
    
    if (!trade.expires_at) {
      return 'Не установлено';
    }
    
    try {
      // Используем текущее время из состояния (обновляется каждую секунду)
      const now = currentTime;
      let expiresAt;
      
      // Обрабатываем expires_at как строку ISO или timestamp
      if (typeof trade.expires_at === 'string') {
        expiresAt = new Date(trade.expires_at);
      } else {
        expiresAt = new Date(trade.expires_at);
      }
      
      // Проверяем валидность даты
      if (isNaN(expiresAt.getTime())) {
        console.warn('Invalid expires_at:', trade.expires_at, 'for trade:', trade.id);
        return 'Ошибка времени';
      }
      
      // Рассчитываем разницу в миллисекундах
      const diffMs = expiresAt.getTime() - now.getTime();
      const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
      
      if (totalSeconds <= 0) {
        return 'Истек';
      }
      
      // Форматируем время
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      
      if (hours > 0) {
        return `${hours}ч ${minutes}м ${seconds}с`;
      } else if (minutes > 0) {
        return `${minutes}м ${seconds}с`;
      } else {
        return `${seconds}с`;
      }
      
    } catch (error) {
      console.error('Error in getTimeRemaining:', error, 'Trade:', trade);
      return 'Ошибка';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">{tt('История сделок')}</h2>
        
      </div>

      {filteredTrades.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h3 className="text-xl mb-2">{tt('Сделки не найдены')}</h3>
          <p>{tt('Начните торговать, чтобы увидеть историю здесь')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {currentTrades.map((trade, index) => (
            <div
              key={trade.id || index}
              className="bg-gray-800 rounded-lg p-4 border-l-4 border-l-blue-400"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* Direction Icon */}
                  <div className={`p-2 rounded-full ${
                    trade.direction === 'call' 
                      ? 'bg-green-600/20 text-green-400' 
                      : 'bg-red-600/20 text-red-400'
                  }`}>
                    {trade.direction === 'call' ? (
                      <ArrowUpCircle className="w-5 h-5" />
                    ) : (
                      <ArrowDownCircle className="w-5 h-5" />
                    )}
                  </div>

                  <div>
                    <div className="text-white font-semibold">
                      {trade.asset} - {trade.direction === 'call' ? 'ПОКУПКА' : 'ПРОДАЖА'}
                    </div>
                    <div className="text-gray-400 text-sm">
                      {formatTime(trade.createdAt || trade.created_at)}
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  {getStatusBadge(trade)}
                  <div className="text-gray-400 text-sm mt-1">
                    ${trade.amount?.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">
                    {trade.status === 'active' ? 'Осталось:' : 'Длительность:'}
                  </span>
                  <div className="text-white">
                    {trade.status === 'active' && getTimeRemaining(trade) 
                      ? getTimeRemaining(trade) 
                      : (() => {
                          const durationSec = trade.duration || trade.expirationTime || 60;
                          const days = Math.floor(durationSec / 86400);
                          const hours = Math.floor(durationSec / 3600);
                          const minutes = Math.floor(durationSec / 60);
                          
                          if (days >= 1) return `${days}д`;
                          if (hours >= 1) return `${hours}ч`;
                          return `${minutes}м`;
                        })()
                    }
                  </div>
                </div>
                
                <div>
                  <span className="text-gray-400">{tt('Результат:')}</span>
                  <div className="font-semibold">
                    {formatProfit(trade)}
                  </div>
                </div>
              </div>

              {trade.status === 'active' && (
                <div className="mt-3 bg-blue-600/20 rounded p-2">
                  <div className="text-blue-400 text-sm flex items-center space-x-2">
                    <Clock className="w-4 h-4" />
                    <span>
                      Сделка активна. Осталось времени: <strong>{getTimeRemaining(trade) || 'Загрузка...'}</strong>
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    Истекает: {(trade.expiresAt || trade.expires_at) ? formatLocalTime(trade.expiresAt || trade.expires_at) : 'не указано'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Debug: Текущее время: {currentTime.toLocaleString('ru-RU', {
                      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Пагинация */}
      {filteredTrades.length > tradesPerPage && (
        <div className="mt-4 flex items-center justify-center space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded bg-gray-700 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 text-sm"
          >
            ←
          </button>
          
          <div className="flex items-center space-x-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`px-2 py-1 rounded text-sm ${
                  currentPage === pageNum 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {pageNum}
              </button>
            ))}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded bg-gray-700 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 text-sm"
          >
            →
          </button>
          
          <span className="text-gray-400 text-sm ml-4">
            Страница {currentPage} из {totalPages} ({filteredTrades.length} сделок)
          </span>
        </div>
      )}
    </div>
  );
};

export default TradeHistory;